import React, { useEffect, useState } from 'react';
import { Bot, User } from 'lucide-react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useLogos } from '../../hooks/useLogos';
import { formatOpenAIResponse, formatOpenAIResponseWithProducts } from '../../utils/textFormatting';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useStreamingResponse } from '../../hooks/useStreamingResponse';
import { useProductSystem } from '../../hooks/useProductSystem';
import type { Message } from '../../types/chat';

// Étendre le type Message pour inclure le champ enrichedHtml
interface EnhancedMessage extends Message {
  enrichedHtml?: string;
  hasProducts?: boolean;
}

interface ChatBubbleProps {
  message: EnhancedMessage;
}

export function ChatBubble({ message }: ChatBubbleProps) {
  const { colors } = useDesignColors();
  const { botIcon } = useLogos();
  const { theme } = useThemeSettings();
  const { chatbotPublicId } = useChatbot();
  const { isStreaming, isProcessingProducts } = useStreamingResponse();
  const { settings: productSettings } = useProductSystem();
  const isBot = message.type === 'bot';
  const isDark = theme.mode === 'dark';
  const [formattedContent, setFormattedContent] = useState<string>('');
  const [isFormatting, setIsFormatting] = useState(false);
  
  const time = new Date(message.timestamp).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Configurer les variables CSS pour les gradients dès le chargement
  useEffect(() => {
    if (colors) {
      document.documentElement.style.setProperty('--gradient-start', colors.gradientStart);
      document.documentElement.style.setProperty('--gradient-end', colors.gradientEnd);
      
      const startRgb = hexToRgb(colors.gradientStart);
      const endRgb = hexToRgb(colors.gradientEnd);
      if (startRgb) document.documentElement.style.setProperty('--gradient-start-rgb', startRgb);
      if (endRgb) document.documentElement.style.setProperty('--gradient-end-rgb', endRgb);
    }
  }, [colors]);

  // Fonction pour convertir hex en rgb
  const hexToRgb = (hex: string): string | null => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const formattedHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(formattedHex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
  };

  // Formater le contenu de manière asynchrone si c'est un message bot
  useEffect(() => {
    if (!isBot) {
      setFormattedContent('');
      return;
    }
    
    // Si le message a déjà un HTML enrichi, l'utiliser directement
    if (message.enrichedHtml) {
      setFormattedContent(message.enrichedHtml);
      return;
    }
    
    // Pendant le streaming, utiliser uniquement le formatage de base
    if (isStreaming) {
      setFormattedContent(formatOpenAIResponse(message.content));
      return;
    }

    // Une fois le streaming terminé, appliquer le formatage complet
    setFormattedContent(formatOpenAIResponse(message.content));
    
    // Ne pas essayer de détecter les produits si nous sommes en train de streamer ou de traiter
    if (chatbotPublicId && !message.enrichedHtml && !isProcessingProducts) {
      formatOpenAIResponseWithProducts(message.content, chatbotPublicId)
        .then(enrichedContent => {
          // Ne mettre à jour que si le contenu est différent
          if (enrichedContent !== formattedContent) {
            setFormattedContent(enrichedContent);
            // Ajouter des CSS vars pour les couleurs de gradient
            document.documentElement.style.setProperty('--gradient-start', colors.gradientStart);
            document.documentElement.style.setProperty('--gradient-end', colors.gradientEnd);
            // Créer les variables RVB pour les dégradés avec transparence
            const startRgb = hexToRgb(colors.gradientStart);
            const endRgb = hexToRgb(colors.gradientEnd);
            if (startRgb) document.documentElement.style.setProperty('--gradient-start-rgb', startRgb);
            if (endRgb) document.documentElement.style.setProperty('--gradient-end-rgb', endRgb);
          }
        })
        .catch(err => {
          // Silent error handling
        });
    }
  }, [
    isBot,
    message.content,
    message.enrichedHtml,
    chatbotPublicId,
    isStreaming,
    isProcessingProducts,
    formattedContent,
    colors,
    productSettings,
  ]);

  // Ajouter un handler pour les clics sur les cartes produit après le rendu
  useEffect(() => {
    if (isBot && formattedContent) {
      const handleProductCardClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        // Trouver l'élément avec data-product-id
        const productElement = target.closest('[data-product-id]');
        
        if (productElement) {
          // Ne pas traiter les clics sur les boutons qui ont déjà leur propre gestionnaire
          if (target.tagName && target.tagName.toLowerCase() === 'button') {
            return;
          }
          if (target.closest('button')) {
            return;
          }
          
          // Extraire l'ID du produit
          const productId = productElement.getAttribute('data-product-id');
          if (productId) {
            // Essayer toutes les méthodes pour ouvrir la modal
            try {
              // 1. Utiliser EventBus
              if (typeof window !== 'undefined' && (window as any).EventBus) {
                (window as any).EventBus.emit('openProductModal', { productId });
              }
              
              // 2. Utiliser CustomEvent
              const openModalEvent = new CustomEvent('open-product-modal', {
                detail: { productId },
                bubbles: true
              });
              document.dispatchEvent(openModalEvent);

              // 3. Utiliser la fonction de fallback globale
              setTimeout(() => {
                if (typeof window !== 'undefined' && typeof (window as any).__openProductModalFallback === 'function') {
                  (window as any).__openProductModalFallback(productId);
                }
              }, 100);
            } catch (err) {
              // Silent error handling
            }
            
            // Empêcher la propagation de l'événement
            event.preventDefault();
            event.stopPropagation();
          }
        }
      };
      
      // Ajouter l'écouteur d'événements
      document.addEventListener('click', handleProductCardClick);
      
      // Nettoyer l'écouteur d'événements
      return () => {
        document.removeEventListener('click', handleProductCardClick);
      };
    }
  }, [isBot, formattedContent]);

  const style = {
    '--gradient-start': colors.gradientStart,
    '--gradient-end': colors.gradientEnd
  } as React.CSSProperties;

  // On vérifie si c'est le dernier message en cours de streaming
  const isStreamingMessage = isBot && isStreaming && !message.enrichedHtml;

  return (
    <div className={`flex ${isBot ? 'justify-start pr-2 md:pr-3' : 'justify-end pl-2 md:pl-3'} items-start gap-2 my-3`} style={style}>
      {isBot && (
        <div className="w-6 h-6 rounded-lg overflow-hidden flex items-center justify-center shrink-0"
          style={{
            background: botIcon ? 'transparent' : `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
            opacity: botIcon ? 1 : 0.1,
          }}>
          {botIcon ? (
            <img src={botIcon} alt="Bot" className="w-full h-full object-cover" />
          ) : (
            <Bot className="w-4 h-4" style={{ color: colors.gradientStart }} />
          )}
        </div>
      )}
      
      <div className="flex flex-col gap-1 max-w-[97%] md:max-w-[94%] lg:max-w-[97%]">
        <div
          style={isBot ? undefined : {
            background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
            opacity: 0.9,
          }}
          className={`rounded-lg px-3 py-2 ${
            isBot
              ? isDark 
                ? 'bg-gray-800/95 text-gray-200' 
                : 'bg-gray-100 text-gray-900'
              : 'text-white'
          } ${isStreamingMessage ? 'border-l-2 border-[var(--gradient-start)]' : ''}`}
        >
          {isBot ? (
            <div 
              className={`prose prose-sm max-w-none [&_p]:mb-1.5 [&_p]:leading-normal [&_ul]:my-1.5 [&_li]:ml-2 [&_li]:my-0.5 
                [&_img]:w-full [&_img]:h-auto [&_img]:object-cover [&_img]:rounded-lg 
                
                [&_.product-card]:relative [&_.product-card]:p-3 [&_.product-card]:rounded-md [&_.product-card]:my-2
                [&_.product-card]:bg-white [&_.product-card]:dark:bg-gray-800/50 
                [&_.product-card]:border [&_.product-card]:border-[var(--gradient-start)]/20
                [&_.product-card]:cursor-pointer [&_.product-card]:overflow-hidden
                [&_.product-card:hover]:scale-[1.01] [&_.product-card:hover]:shadow-md
                [&_.product-card]:transition-all [&_.product-card]:duration-200
                
                [&_.product-header]:flex [&_.product-header]:justify-between [&_.product-header]:mb-3
                [&_.product-title]:font-bold [&_.product-title]:text-base [&_.product-title]:bg-gradient-to-r [&_.product-title]:from-[var(--gradient-start)] [&_.product-title]:to-[var(--gradient-end)] [&_.product-title]:bg-clip-text [&_.product-title]:text-transparent
                [&_.product-content]:flex [&_.product-content]:items-start [&_.product-content]:gap-3
                
                [&_.product-image-container]:relative [&_.product-image-container]:flex-shrink-0
                [&_.product-image]:w-28 [&_.product-image]:h-28 [&_.product-image]:rounded-md [&_.product-image]:object-cover [&_.product-image]:shadow-sm
                
                [&_.product-discount]:absolute [&_.product-discount]:top-0 [&_.product-discount]:left-0 [&_.product-discount]:w-full
                [&_.product-discount]:bg-gradient-to-r [&_.product-discount]:from-[var(--gradient-start)] [&_.product-discount]:to-[var(--gradient-end)]
                [&_.product-discount]:text-white [&_.product-discount]:text-xs [&_.product-discount]:font-bold [&_.product-discount]:py-0.5 [&_.product-discount]:text-center
                
                [&_.product-info]:flex-1 [&_.product-info]:flex [&_.product-info]:flex-col [&_.product-info]:gap-1
                [&_.product-description]:text-sm [&_.product-description]:text-gray-700 [&_.product-description]:dark:text-gray-300 [&_.product-description]:opacity-90 [&_.product-description]:line-clamp-2
                
                ${productSettings?.showPrices ? `
                  [&_.product-price-container]:flex [&_.product-price-container]:items-baseline [&_.product-price-container]:gap-2 [&_.product-price-container]:mt-1
                  [&_.product-price]:text-lg [&_.product-price]:font-semibold [&_.product-price]:bg-gradient-to-r [&_.product-price]:from-[var(--gradient-start)] [&_.product-price]:to-[var(--gradient-end)] [&_.product-price]:bg-clip-text [&_.product-price]:text-transparent
                  [&_.product-original-price]:text-sm [&_.product-original-price]:line-through [&_.product-original-price]:opacity-70
                ` : `
                  [&_.product-price-container]:hidden
                  [&_.product-price]:hidden
                  [&_.product-original-price]:hidden
                `}
                
                ${productSettings?.showStock ? `
                  [&_.product-stock]:text-xs [&_.product-stock]:px-2 [&_.product-stock]:py-0.5 [&_.product-stock]:rounded-sm [&_.product-stock]:bg-[var(--gradient-start)]/10 [&_.product-stock]:text-[var(--gradient-start)] [&_.product-stock]:inline-block [&_.product-stock]:mt-1
                ` : `
                  [&_.product-stock]:hidden
                `}
                
                [&_.product-button]:absolute [&_.product-button]:bottom-3 [&_.product-button]:right-3 
                [&_.product-button]:bg-gradient-to-r [&_.product-button]:from-[var(--gradient-start)] [&_.product-button]:to-[var(--gradient-end)]
                [&_.product-button]:text-white [&_.product-button]:font-medium [&_.product-button]:text-sm 
                [&_.product-button]:p-2 [&_.product-button]:rounded-full
                [&_.product-button]:shadow-md [&_.product-button:hover]:shadow-lg [&_.product-button:hover]:opacity-90
                
                ${isStreamingMessage ? 'streaming' : ''}`}
              dangerouslySetInnerHTML={{ 
                __html: formattedContent
              }} 
            />
          ) : (
            <p className="break-words leading-normal">{message.content}</p>
          )}
          
          {/* Afficher l'indicateur de chargement uniquement pendant le traitement des produits */}
          {isBot && isProcessingProducts && !isStreaming && (
            <div className="flex items-center space-x-1 mt-2 text-xs text-[var(--gradient-start)]/70">
              <div className="w-1 h-1 bg-[var(--gradient-start)]/70 rounded-full animate-pulse"></div>
              <div className="w-1 h-1 bg-[var(--gradient-start)]/70 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-1 h-1 bg-[var(--gradient-start)]/70 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          )}
        </div>
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} px-2`}>
          {time}
        </span>
      </div>

      {!isBot && (
        <div 
          className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
            opacity: 0.9,
          }}
        >
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
}